// src/components/news/NewsCommentComposer.js

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import placeholderProfile from '../../assets/placeholder-profile.png';

import './NewsCommentComposer.css';

const MAX_COMMENT_LENGTH = 1000;
const MIN_TEXTAREA_HEIGHT = 42;
const MAX_TEXTAREA_HEIGHT = 132;

const NewsCommentComposer = forwardRef(
  function NewsCommentComposer(
    {
      user,
      mode = 'comment',
      replyingTo = null,
      submitting = false,
      error = '',
      autoFocus = false,
      useExternalActions = false,
      onSubmit,
      onCancel,
      onStateChange,
      onFocusChange,
    },
    forwardedRef
  ) {
    const textareaRef =
      useRef(null);

    const [
      text,
      setText,
    ] = useState('');

    const [
      focused,
      setFocused,
    ] = useState(false);

    const isReply =
      mode === 'reply';

    const trimmedText =
      text.trim();

    const charactersRemaining =
      MAX_COMMENT_LENGTH -
      text.length;

    const canSubmit =
      Boolean(trimmedText) &&
      !submitting &&
      text.length <=
        MAX_COMMENT_LENGTH;

    const profilePic =
      user?.profilePic ||
      placeholderProfile;

    /**
     * Keep the textarea compact initially,
     * then grow it as additional lines are added.
     */
    const resizeTextarea =
      (element) => {
        if (!element) {
          return;
        }

        element.style.height =
          'auto';

        const nextHeight =
          Math.min(
            Math.max(
              element.scrollHeight,
              MIN_TEXTAREA_HEIGHT
            ),
            MAX_TEXTAREA_HEIGHT
          );

        element.style.height =
          `${nextHeight}px`;

        element.style.overflowY =
          element.scrollHeight >
          MAX_TEXTAREA_HEIGHT
            ? 'auto'
            : 'hidden';
      };

    useEffect(() => {
      if (
        autoFocus &&
        textareaRef.current
      ) {
        textareaRef.current.focus();
      }
    }, [autoFocus]);

    /**
     * Reset the composer whenever the mode or reply
     * target changes.
     *
     * This keeps one fixed composer clean when moving:
     * comment → reply → another reply.
     */
    useEffect(() => {
      setText('');

      requestAnimationFrame(
        () => {
          resizeTextarea(
            textareaRef.current
          );
        }
      );
    }, [
      mode,
      replyingTo?.commentId,
    ]);

    /**
     * Tell NewsDetailScreen whether the fixed
     * footer can currently submit.
     */
    useEffect(() => {
      if (
        typeof onStateChange !==
        'function'
      ) {
        return;
      }

      onStateChange({
        text,
        textLength:
          text.length,
        charactersRemaining,
        canSubmit,
        hasText:
          Boolean(trimmedText),
        focused,
      });
    }, [
      text,
      trimmedText,
      charactersRemaining,
      canSubmit,
      focused,
      onStateChange,
    ]);

    const handleChange =
      (event) => {
        const nextValue =
          event.target.value ||
          '';

        if (
          nextValue.length >
          MAX_COMMENT_LENGTH
        ) {
          return;
        }

        setText(nextValue);

        resizeTextarea(
          event.target
        );
      };

    const clearComposer =
      () => {
        setText('');

        requestAnimationFrame(
          () => {
            resizeTextarea(
              textareaRef.current
            );
          }
        );
      };

    const handleSubmit =
      async () => {
        if (
          !canSubmit ||
          typeof onSubmit !==
            'function'
        ) {
          return false;
        }

        const result =
          await onSubmit(
            trimmedText
          );

        if (result !== false) {
          clearComposer();
          return true;
        }

        return false;
      };

    const handleCancel =
      () => {
        clearComposer();

        textareaRef.current?.blur();

        if (
          typeof onCancel ===
          'function'
        ) {
          onCancel();
        }
      };

    const handleKeyDown =
      (event) => {
        if (
          event.key !== 'Enter' ||
          event.shiftKey
        ) {
          return;
        }

        /**
         * The fixed News footer always uses the
         * external send button.
         *
         * Enter should therefore stay available for
         * a new line in both comment and reply mode.
         */
        if (useExternalActions) {
          return;
        }

        event.preventDefault();

        handleSubmit();
      };

    const handleFocus =
      () => {
        setFocused(true);

        if (
          typeof onFocusChange ===
          'function'
        ) {
          onFocusChange(true);
        }
      };

    const handleBlur =
      () => {
        setFocused(false);

        if (
          typeof onFocusChange ===
          'function'
        ) {
          onFocusChange(false);
        }
      };

    /**
     * NewsDetailScreen uses these methods for its
     * fixed footer controls.
     */
    useImperativeHandle(
      forwardedRef,
      () => ({
        submit:
          handleSubmit,

        cancel:
          handleCancel,

        focus: () => {
          textareaRef.current?.focus();
        },

        clear:
          clearComposer,

        canSubmit:
          () => canSubmit,

        getText:
          () => text,
      }),
      [
        canSubmit,
        text,
        submitting,
      ]
    );

    return (
      <div
        className={`news-comment-composer ${
          isReply
            ? 'news-comment-composer--reply'
            : 'news-comment-composer--main'
        } ${
          useExternalActions
            ? 'news-comment-composer--external-actions'
            : ''
        }`}
      >
        <div className="news-comment-composer-main">
          <img
            src={profilePic}
            alt=""
            className="news-comment-composer-avatar"
            onError={(event) => {
              event.currentTarget.src =
                placeholderProfile;
            }}
          />

          <div className="news-comment-composer-content">
            <div className="news-comment-input-shell">
              <textarea
                ref={textareaRef}
                className="news-comment-textarea"
                placeholder={
                  isReply
                    ? 'Write a reply...'
                    : 'Share your thoughts...'
                }
                value={text}
                maxLength={
                  MAX_COMMENT_LENGTH
                }
                disabled={
                  submitting
                }
                rows={1}
                onChange={
                  handleChange
                }
                onKeyDown={
                  handleKeyDown
                }
                onFocus={
                  handleFocus
                }
                onBlur={
                  handleBlur
                }
                aria-label={
                  isReply
                    ? 'Write a reply'
                    : 'Write a comment'
                }
              />

              {!useExternalActions && (
                <div className="news-comment-composer-bottom">
                  <button
                    type="button"
                    className="news-comment-submit-btn"
                    onClick={
                      handleSubmit
                    }
                    disabled={
                      !canSubmit
                    }
                  >
                    {submitting
                      ? isReply
                        ? 'Replying…'
                        : 'Posting…'
                      : isReply
                      ? 'Reply'
                      : 'Post'}
                  </button>
                </div>
              )}
            </div>

            {error && (
              <p
                className="news-comment-composer-error"
                role="alert"
              >
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default NewsCommentComposer;
